import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { ControlBarButton } from '@m12s/component-library';
import { Box } from '@chakra-ui/react';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import { check, signOut, user, sync, handPointer, random } from 'lib/icons';
import ConfirmDialog from 'components/ConfirmDialog';
import BlockingOverlay from 'components/BlockingOverlay';
import { UtilityMenu } from 'components/UtilityMenu';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import useSelectOperator from 'lib/hooks/useSelectOperator';
import Routes from 'lib/utils/routes';
import TokenStore from 'lib/api/links/TokenStore';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import MMDrawer from 'components/MMDrawer';
import EmbeddedMMDrawer from 'components/EmbeddedMMDrawer';
import { getMachine } from 'lib/selectors/getMachine';
import { getCompany } from 'lib/selectors/getCompany';
import { getIsErpDriven } from 'lib/selectors/getIsErpDriven';
import { getEffectiveErpResources } from 'lib/selectors/getEffectiveErpResources';
import { getSevenDayLookbackStart } from 'lib/selectors/getSevenDayLookbackStart';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getIsSetupModeActive } from 'lib/selectors/getIsSetupModeActive';
import { LABOR_TICKET_HOST } from 'lib/appConfig';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import { throwErrorToast } from 'lib/utils/toast';
import { START_SETUP_MODE, END_SETUP_MODE } from 'lib/api/queries';
import {
  actionOpenLaborTicketModal,
  actionSetEmbeddedLaborTicket,
  actionSetSetupMode,
} from 'lib/actions';
import { useRegisterOperatorOverlay } from 'lib/hooks';
import { MultiJobSelectFlow } from 'components/MultiJobSelect';
import { getHasCards } from 'lib/selectors/getHasCards';
import { getProgramEvents } from 'lib/selectors/getProgramEvents';
import { getTimeOptionsContext } from 'lib/selectors/getTimeOptionsContext';
import { getLaborFormAllowRejects } from 'lib/selectors/getLaborFormAllowRejects';
import { CategorizeDowntime } from './ERPJobControlBar/CategorizeDowntime';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  Scrollable,
  SingleButtonScrollable,
} from './styled';

/**
 * Control bar for APM multi-job (palletized) machines.
 *
 * Production mode: Enter Setup, [Submit all when jobs running], Categorize, Sign out.
 * Setup mode: Enter Prod., Submit all / Select jobs, Categorize, Sign out.
 * ERP-driven machines never show Sign In / Sign Out — the ERP dispatches jobs
 * and labor, so signed-out operators get downtime categorization only (KGM-119).
 */
const ERPMultiJobControlBar = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const company = useSelector(getCompany);
  const isErpDriven = useSelector(getIsErpDriven);
  const erpResources = useSelector(getEffectiveErpResources);
  const machineRef = machine?.machineRef;
  const lookbackStart = useSelector(getSevenDayLookbackStart);
  const operator = useSelector(getCurrentOperator);
  const currentOperatorErpId = operator?.erpId ?? null;
  const isSetupMode = useSelector(getIsSetupModeActive);
  const dispatch = useDispatch();
  const requestOperatorLogout = useOperatorLogout();
  const { openSelectOperator } = useSelectOperator();
  const [startSetupModeMutation] = useMutation(START_SETUP_MODE);
  const [endSetupModeMutation] = useMutation(END_SETUP_MODE);

  const hasCardsFromData = useSelector(getHasCards);
  const programEvents = useSelector(getProgramEvents);
  const timeOptionsContext = useSelector(getTimeOptionsContext);
  const allowRejects = useSelector(getLaborFormAllowRejects);
  const hasOpenLaborTickets = useSelector((state) => {
    return state.workOrderManagement.hasOpenLaborTickets;
  });
  const hasOpenSetupLaborTickets = useSelector((state) => {
    return state.workOrderManagement.hasOpenSetupLaborTickets;
  });

  // There is something to submit only when a program interval has no labor
  // ticket yet (unsubmitted), or an open labor ticket is still clocked in.
  // Confirmed-but-fully-submitted cards leave nothing to submit, so "Submit
  // all" must be disabled rather than open an empty flyup. Mirrors the
  // submit-all screen's own "is there any item" rule (laborTicketRef == null).
  const hasUnsubmittedIntervals = useMemo(() => {
    return (programEvents || []).some((event) => {
      // Blocked/closed jobs are excluded from Submit all, so their unsubmitted
      // intervals must not count toward enabling the button (else it opens an
      // empty flyup).
      const status = (event.status || '').toUpperCase();
      const isClosed =
        event.closedDate != null ||
        status === 'COMPLETED' ||
        status === 'CANCELLED';
      if (isClosed) return false;
      return (event.intervals || []).some((iv) => {
        return iv.voidedAt == null && iv.laborTicketRef == null;
      });
    });
  }, [programEvents]);
  const hasSubmittableWork = hasOpenLaborTickets || hasUnsubmittedIntervals;
  const [submitAllOpen, setSubmitAllOpen] = useState(false);
  const [selectJobsOpen, setSelectJobsOpen] = useState(false);
  const [addJobOpen, setAddJobOpen] = useState(false);
  const [openedFromSubmitAll, setOpenedFromSubmitAll] = useState(false);
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [eventFormContext, setEventFormContext] = useState(null);
  const eventFormEmbedContext = useMemo(() => {
    return eventFormContext && { ...eventFormContext, allowRejects };
  }, [eventFormContext, allowRejects]);
  const [submitAllRefreshToken, setSubmitAllRefreshToken] = useState(0);

  // These drawers live in local state, so register their open/closed state
  // centrally for the downtime watcher to suppress the prompt while any is on
  // screen (the hook clears the flag if this bar unmounts while one is open).
  useRegisterOperatorOverlay('submit-all-drawer', submitAllOpen);
  useRegisterOperatorOverlay('program-event-drawer', eventFormOpen);
  useRegisterOperatorOverlay('select-jobs-drawer', selectJobsOpen);
  useRegisterOperatorOverlay('add-job-drawer', addJobOpen);
  /** True only when Submit-all was opened via Sign out (not the Submit all bar button). */
  const [submitAllOpenedFromSignOut, setSubmitAllOpenedFromSignOut] = useState(
    false
  );
  const [
    submitAllOpenedFromEnterProd,
    setSubmitAllOpenedFromEnterProd,
  ] = useState(false);
  const pendingItemCountRef = useRef(0);
  const pendingDismissIdRef = useRef(null);
  // Active setup submit-all cycle: the ordered setup tickets + current index.
  // While set, submitting a setup ticket advances to the next one's form
  // instead of dropping back to the submit-all list.
  const setupCycleRef = useRef(null);
  const [dismissedItemIds, setDismissedItemIds] = useState([]);
  /** Confirmation modal for setup⇄production transitions: 'setup' | 'production' | null */
  const [pendingTransition, setPendingTransition] = useState(null);
  /**
   * Opaque transition overlay shown while a setup⇄production switch is in
   * flight: 'setup' | 'production' | null. Covers the viewport (control bar +
   * embedded board) from the moment the operator confirms until the switch
   * settles, so there is no stale UI beneath a just-dismissed modal to tap
   * through into (e.g. spuriously opening Submit all / Select jobs / a card).
   */
  const [transitioning, setTransitioning] = useState(null);
  // The Enter Setup/Prod confirm dialog and its in-flight blocking overlay are
  // operator flows too, so suppress the downtime prompt while either is up.
  useRegisterOperatorOverlay(
    'enter-mode-transition',
    Boolean(pendingTransition) || Boolean(transitioning)
  );

  const handleEnterSetup = useCallback(async () => {
    try {
      const { data } = await startSetupModeMutation({
        variables: { input: { machineRef } },
      });
      dispatch(actionSetSetupMode(data?.startSetupMode || null));
      // Production→setup must never leave the Submit-all drawer open — the
      // drawer belongs only to the setup→production / sign-out gates. Close it
      // affirmatively so a stray tap (see the tap shield below) can't strand it.
      setSubmitAllOpen(false);
      setSubmitAllOpenedFromEnterProd(false);
      window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
    } catch {
      // Mutation failed; setup mode state unchanged
    }
  }, [startSetupModeMutation, machineRef, dispatch]);

  const endingSetupRef = useRef(false);
  const endSetupMode = useCallback(async () => {
    // Guard against double-fire: the transition watcher below and the
    // submit-all all-submitted handler can both observe the same drain.
    if (endingSetupRef.current) return;
    endingSetupRef.current = true;
    try {
      const { data } = await endSetupModeMutation({
        variables: { input: { machineRef } },
      });
      dispatch(actionSetSetupMode(data?.endSetupMode || null));
      window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
    } catch {
      // Mutation failed; remain in setup mode
    } finally {
      endingSetupRef.current = false;
    }
  }, [endSetupModeMutation, machineRef, dispatch]);

  // Submitting the LAST open setup ticket only moves the machine forward when
  // the operator explicitly asked it to. The setup "Submit all" screen always
  // drains its tickets through the host setup-cycle (it can't tell us which bar
  // button opened it and never emits `all-submitted` for setup), so this drain
  // is the single point where that intent is honored:
  //   • Entered via "Enter Prod." → end setup mode (move into production).
  //   • Entered via "Sign out"    → log the operator out. The batch "Submit
  //     all" screen drops the sign-out flag (submit-setup-cycle carries no
  //     isSignOut), so it never reaches the per-ticket OPERATOR_LOGOUT action;
  //     this is where that path logs out. Like OPERATOR_LOGOUT, it does NOT end
  //     setup mode — signing out mid-setup must not push the machine to prod.
  //   • Any other surface — a plain "Submit all", an individual ticket form —
  //     leaves the machine in setup mode; the operator taps "Enter Prod." when
  //     they are ready. A plain "Submit all" must NOT auto-transition.
  // Transition-triggered (true → false) so being in setup mode with no tickets
  // created yet does not bounce straight out.
  const prevHasOpenSetupLtsRef = useRef(hasOpenSetupLaborTickets);
  useEffect(() => {
    const prev = prevHasOpenSetupLtsRef.current;
    prevHasOpenSetupLtsRef.current = hasOpenSetupLaborTickets;
    const drainedLastSetupTicket =
      isSetupMode && prev && !hasOpenSetupLaborTickets;
    if (!drainedLastSetupTicket) return;
    if (submitAllOpenedFromEnterProd) {
      setSubmitAllOpenedFromEnterProd(false);
      endSetupMode();
    } else if (submitAllOpenedFromSignOut) {
      setSubmitAllOpenedFromSignOut(false);
      requestOperatorLogout();
    }
  }, [
    hasOpenSetupLaborTickets,
    isSetupMode,
    endSetupMode,
    submitAllOpenedFromEnterProd,
    submitAllOpenedFromSignOut,
    requestOperatorLogout,
  ]);

  const handleEnterProd = useCallback(async () => {
    if (!hasOpenSetupLaborTickets) {
      await endSetupMode();
      return;
    }
    setDismissedItemIds([]);
    setSubmitAllOpenedFromEnterProd(true);
    setSubmitAllOpenedFromSignOut(false);
    setSubmitAllOpen(true);
  }, [endSetupMode, hasOpenSetupLaborTickets]);

  // Enter Setup / Enter Prod. are confirmed via a modal first. Entering
  // production still routes open setup tickets through Submit-all (handleEnterProd);
  // entering setup pauses the production job with no submission required.
  const handleConfirmTransition = useCallback(async () => {
    const transition = pendingTransition;
    setPendingTransition(null);
    if (transition !== 'setup' && transition !== 'production') return;
    // Cover the viewport until the switch settles so the confirm tap can't
    // pass through to stale UI underneath the dismissed modal.
    setTransitioning(transition);
    try {
      if (transition === 'setup') {
        await handleEnterSetup();
      } else {
        await handleEnterProd();
      }
    } finally {
      setTransitioning(null);
    }
  }, [pendingTransition, handleEnterSetup, handleEnterProd]);

  const handleSignOut = useCallback(() => {
    if (!hasOpenSetupLaborTickets) {
      requestOperatorLogout();
      return;
    }
    setDismissedItemIds([]);
    setSubmitAllOpenedFromSignOut(true);
    setSubmitAllOpenedFromEnterProd(false);
    setSubmitAllOpen(true);
  }, [hasOpenSetupLaborTickets, requestOperatorLogout]);

  const handleSubmitAll = useCallback(() => {
    setDismissedItemIds([]);
    setSubmitAllOpenedFromSignOut(false);
    setSubmitAllOpenedFromEnterProd(false);
    setSubmitAllOpen(true);
  }, []);

  // Add Job (hamburger) → apm-select-jobs multi-select "Select jobs to set up"
  useEffect(() => {
    const handler = () => {
      setAddJobOpen(true);
    };
    window.addEventListener('apm:add-job', handler);
    return () => {
      return window.removeEventListener('apm:add-job', handler);
    };
  }, []);

  // Assign unknown job (e.g. UnknownJob footer) → same apm-select-jobs drawer as "Select Jobs"
  useEffect(() => {
    const handler = () => {
      setSelectJobsOpen(true);
    };
    window.addEventListener('apm:open-apm-select-jobs', handler);
    return () => {
      return window.removeEventListener('apm:open-apm-select-jobs', handler);
    };
  }, []);

  const closeSubmitAll = useCallback(() => {
    setSubmitAllOpen(false);
    setSubmitAllOpenedFromEnterProd(false);
  }, []);
  const closeSelectJobs = useCallback(() => {
    setSelectJobsOpen(false);
    window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
  }, []);
  const closeAddJob = useCallback(() => {
    setAddJobOpen(false);
    window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
  }, []);
  const handleReturnFromForm = useCallback(
    (reopenSubmitAll, didSubmit = false) => {
      if (openedFromSubmitAll) {
        setOpenedFromSubmitAll(false);
        if (reopenSubmitAll) {
          if (didSubmit) {
            const dismissId = pendingDismissIdRef.current;
            if (dismissId) {
              setDismissedItemIds((prev) => {
                return [...prev, dismissId];
              });
              pendingDismissIdRef.current = null;
            }
            setSubmitAllRefreshToken((token) => {
              return token + 1;
            });
            window.dispatchEvent(
              new CustomEvent('apm:opdash-refresh', {
                detail: { dismissItemId: dismissId },
              })
            );
            if (pendingItemCountRef.current > 1) {
              setSubmitAllOpen(true);
            }
          } else {
            setSubmitAllOpen(true);
          }
        }
      }
    },
    [openedFromSubmitAll]
  );

  const handleEventFormClose = useCallback(() => {
    setEventFormOpen(false);
    setEventFormContext(null);
    handleReturnFromForm(true, false);
  }, [handleReturnFromForm]);

  const handleEventFormSubmit = useCallback(() => {
    setEventFormOpen(false);
    setEventFormContext(null);
    handleReturnFromForm(true, true);
  }, [handleReturnFromForm]);

  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-screen:close',
    closeSubmitAll
  );

  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-form-submit-all:all-submitted',
    useCallback(async () => {
      if (submitAllOpenedFromEnterProd) {
        await endSetupMode();
        setSubmitAllOpenedFromEnterProd(false);
        setSubmitAllOpen(false);
      } else if (submitAllOpenedFromSignOut) {
        if (isSetupMode) {
          await endSetupMode();
        }
        setSubmitAllOpenedFromSignOut(false);
        setSubmitAllOpen(false);
        requestOperatorLogout();
      } else {
        setSubmitAllOpen(false);
      }
    }, [
      submitAllOpenedFromEnterProd,
      submitAllOpenedFromSignOut,
      isSetupMode,
      endSetupMode,
      requestOperatorLogout,
    ])
  );

  // Split from the labor-ticket form: close submit-all and hand off to the
  // run-details drawer (owned by MachineMain) opened straight into split mode
  // for this run. Coordinated via a window event since the two drawers live in
  // sibling components — mirrors the split-change-job round trip's approach.
  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-form-submit-all:split-job',
    useCallback(
      (data) => {
        if (!currentOperatorErpId) {
          throwErrorToast('Sign in to submit labor');
          return;
        }
        if (data?.workOrderOperationRef == null) {
          return;
        }
        setSubmitAllOpen(false);
        window.dispatchEvent(
          new CustomEvent('apm:open-run-details-split', {
            detail: {
              machineRef: data.machineRef,
              workOrderOperationRef: data.workOrderOperationRef,
            },
          })
        );
      },
      [currentOperatorErpId]
    )
  );

  // When a ticket row is clicked, close submit-all drawer and open the appropriate form
  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-form-submit-all:open-ticket',
    useCallback(
      (data) => {
        if (!currentOperatorErpId) {
          throwErrorToast('Sign in to submit labor');
          return;
        }

        pendingItemCountRef.current = data?.itemCount || 0;
        pendingDismissIdRef.current = data?.eventId
          ? `event-${data.eventId}`
          : data?.laborTicketRef
          ? `lt-${data.laborTicketRef}`
          : null;

        const laborTicketAction =
          data.isSignOut && data.itemCount === 1
            ? LABOR_TICKET_ACTIONS.OPERATOR_LOGOUT
            : 'submit';

        if (data?.laborTicketRef) {
          setSubmitAllOpen(false);
          setOpenedFromSubmitAll(true);
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: currentOperatorErpId,
              workOrderId: data.workOrderId,
              sequenceNumber: data.sequenceNumber,
              resourceId: data.resourceId,
            })
          );
          dispatch(
            actionOpenLaborTicketModal(laborTicketAction, {
              isSignOut: !!data.isSignOut,
            })
          );
        } else if (data?.eventId && data?.machineRef) {
          // Program event without a labor ticket — open the event form
          const eventMachineMatchesCurrent =
            Number(data.machineRef) === Number(machineRef);
          setSubmitAllOpen(false);
          setOpenedFromSubmitAll(true);
          setEventFormContext({
            eventId: data.eventId,
            eventRefs: data.eventRefs,
            machineRef: data.machineRef,
            orderNumber: data.workOrderId,
            partNumber: data.partNumber,
            sequenceNumber: data.sequenceNumber,
            clockIn: data.clockIn,
            personId: currentOperatorErpId,
            workOrderOperationRef: data.workOrderOperationRef,
            type: data.ticketType || 'production',
            isAPMControlled: true,
            opened: true,
            action: laborTicketAction,
            isSignOut: !!data.isSignOut,
            timeOptionsContext: eventMachineMatchesCurrent
              ? timeOptionsContext
              : undefined,
          });
          setEventFormOpen(true);
        }
      },
      [dispatch, currentOperatorErpId, machineRef, timeOptionsContext]
    )
  );

  // Open the setup ticket at `index` in the active cycle (host labor-ticket
  // modal). Used to walk the operator form-to-form during "Submit all" for
  // setup, mirroring the production submit-all's one-at-a-time cadence.
  const openSetupCycleTicket = useCallback(
    (index) => {
      const cycle = setupCycleRef.current;
      const ticket = cycle?.tickets?.[index];
      if (!ticket) return;
      dispatch(
        actionSetEmbeddedLaborTicket({
          laborTicketRef: ticket.laborTicketRef,
          personId: currentOperatorErpId,
          workOrderId: ticket.workOrderId,
          sequenceNumber: ticket.sequenceNumber,
        })
      );
      dispatch(actionOpenLaborTicketModal('submit', { isSignOut: false }));
    },
    [dispatch, currentOperatorErpId]
  );

  const onLaborTicketClose = useCallback(() => {
    // Backed out mid setup-cycle — stop advancing and return to the list so
    // the operator can resume from the remaining tickets.
    if (setupCycleRef.current) {
      setupCycleRef.current = null;
      setSubmitAllOpen(true);
      return;
    }
    handleReturnFromForm(true, false);
  }, [handleReturnFromForm]);
  const onLaborTicketSubmit = useCallback(() => {
    const cycle = setupCycleRef.current;
    if (cycle) {
      const next = cycle.index + 1;
      if (next < cycle.tickets.length) {
        setupCycleRef.current = { tickets: cycle.tickets, index: next };
        openSetupCycleTicket(next);
        return;
      }
      // Last setup ticket submitted — the cycle is done. Whether this ends
      // setup mode (Enter Prod. → production, Sign out → logout) or simply
      // leaves the machine in setup (plain "Submit all") is decided by the
      // hasOpenSetupLaborTickets drain effect above, keyed on how the flow was
      // entered.
      setupCycleRef.current = null;
      return;
    }
    handleReturnFromForm(true, true);
  }, [handleReturnFromForm, openSetupCycleTicket]);
  useEmbeddedEffect(
    'labor-ticket-form',
    'labor-ticket-form:close',
    onLaborTicketClose
  );
  useEmbeddedEffect(
    'labor-ticket-form',
    'labor-ticket-form:submit',
    onLaborTicketSubmit
  );

  // "Submit all" for setup — the submit-all screen hands over the ordered
  // queue of setup tickets; open the first one's form and let onLaborTicketSubmit
  // walk to the next, so setup mirrors the production submit-all's one-at-a-time
  // flow instead of returning to the list after each ticket.
  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-form-submit-all:submit-setup-cycle',
    useCallback(
      (data) => {
        if (!currentOperatorErpId) {
          throwErrorToast('Sign in to submit labor');
          return;
        }
        const tickets = Array.isArray(data?.tickets)
          ? data.tickets.filter((ticket) => {
              return ticket?.laborTicketRef != null;
            })
          : [];
        if (tickets.length === 0) return;
        setupCycleRef.current = { tickets, index: 0 };
        setOpenedFromSubmitAll(false);
        setSubmitAllOpen(false);
        openSetupCycleTicket(0);
      },
      [currentOperatorErpId, openSetupCycleTicket]
    )
  );

  useEmbeddedEffect(
    'labor-ticket-event-form',
    'labor-ticket-form:close',
    handleEventFormClose
  );
  useEmbeddedEffect(
    'labor-ticket-event-form',
    'labor-ticket-form:submit',
    handleEventFormSubmit
  );

  if (!operator) {
    const machineSwitchingEnabled = !!company?.machineSwitching;
    // A "Change machine" button joins the primary action when machine switching
    // is enabled (mirrors the hamburger menu and the demo). Two buttons need the
    // flex row; a lone button stays centered.
    const SignedOutContainer = machineSwitchingEnabled
      ? Scrollable
      : SingleButtonScrollable;
    return (
      <>
        <ControlBarResponsive id="job-control-bar">
          <UtilityMenu />
          <SignedOutContainer>
            {isErpDriven ? (
              // ERP-driven: jobs and labor are dispatched by the ERP, so
              // operators never sign in here — downtime categorization only
              <CategorizeDowntime history={history} machine={machine} />
            ) : (
              <ControlBarButton
                id="job-control-bar-sign-in"
                onClick={() => {
                  openSelectOperator();
                }}
              >
                <MobileIcon icon={user} />
                <ResponsiveText
                  full={t('signIn|full')}
                  medium={t('signIn|medium')}
                  short={t('signIn|short')}
                />
              </ControlBarButton>
            )}
            {machineSwitchingEnabled && (
              <ControlBarButton
                id="job-control-bar-change-machine"
                onClick={() => {
                  // Mirror the hamburger's switch-machine action: clear any
                  // preset machine token, then route to machine selection.
                  new TokenStore('presetMachine').clear();
                  history.push(Routes.switchMachinesPath());
                }}
              >
                <MobileIcon icon={random} />
                <ResponsiveText
                  full={t('changeMachine|full')}
                  medium={t('changeMachine|medium')}
                  short={t('changeMachine|short')}
                />
              </ControlBarButton>
            )}
          </SignedOutContainer>
        </ControlBarResponsive>
      </>
    );
  }

  return (
    <>
      <ControlBarResponsive id="job-control-bar">
        <UtilityMenu />
        <Scrollable>
          {isSetupMode ? (
            <>
              <ControlBarButton
                id="job-control-enter-prod"
                onClick={() => {
                  return setPendingTransition('production');
                }}
              >
                <MobileIcon icon={sync} />
                <ResponsiveText full="Enter Prod." medium="Prod." short="" />
              </ControlBarButton>
              {hasOpenSetupLaborTickets ? (
                <ControlBarButton
                  id="job-control-submit-all"
                  onClick={handleSubmitAll}
                >
                  <MobileIcon icon={check} />
                  <ResponsiveText full="Submit all" medium="Submit" short="" />
                </ControlBarButton>
              ) : (
                <ControlBarButton
                  id="job-control-select-jobs"
                  onClick={() => {
                    setSelectJobsOpen(true);
                  }}
                >
                  <MobileIcon icon={handPointer} />
                  <ResponsiveText full="Select jobs" medium="Select" short="" />
                </ControlBarButton>
              )}
            </>
          ) : (
            <>
              <ControlBarButton
                id="job-control-enter-setup"
                onClick={() => {
                  return setPendingTransition('setup');
                }}
              >
                <MobileIcon icon={sync} />
                <ResponsiveText full="Enter Setup" medium="Setup" short="" />
              </ControlBarButton>
              {(hasOpenLaborTickets || hasCardsFromData) && (
                <ControlBarButton
                  id="job-control-submit-all"
                  disabled={!hasSubmittableWork}
                  onClick={hasSubmittableWork ? handleSubmitAll : undefined}
                >
                  <MobileIcon icon={check} disabled={!hasSubmittableWork} />
                  <ResponsiveText full="Submit all" medium="Submit" short="" />
                </ControlBarButton>
              )}
            </>
          )}

          <CategorizeDowntime history={history} machine={machine} />

          {/* ERP-driven: sign-out is handled by the ERP integration */}
          {!isErpDriven && (
            <ControlBarButton id="signout" onClick={handleSignOut}>
              <MobileIcon icon={signOut} />
              <ResponsiveText
                full={t('signout|full')}
                medium={t('signout|medium')}
                short={t('signout|short')}
              />
            </ControlBarButton>
          )}
        </Scrollable>
      </ControlBarResponsive>

      {/* Submit All drawer (sign-out or enter-prod gate) */}
      <EmbeddedMMDrawer
        embedZoneId="labor-ticket-form-submit-all"
        messageSource="labor-ticket-form"
        src={LABOR_TICKET_HOST}
        path="/submit-all"
        context={{
          machineRef,
          start: lookbackStart,
          personId: currentOperatorErpId,
          erpResources,
          refreshToken: submitAllRefreshToken,
          dismissedItemIds,
          isSignOut: submitAllOpenedFromSignOut,
          setupMode: isSetupMode,
          programEvents,
          timeOptionsContext,
          allowRejects,
        }}
        isOpen={submitAllOpen}
        onDismiss={closeSubmitAll}
        defaultTitle="Unsubmitted work"
      />

      {/* Enter Setup / Enter Production confirmation */}
      {pendingTransition && (
        <ConfirmDialog
          title={
            pendingTransition === 'setup'
              ? t('Enter setup mode?')
              : t('Enter production mode?')
          }
          message={
            pendingTransition === 'setup'
              ? t(
                  "While you're in setup, automatic program matching pauses so it won't track jobs for you. You can set up one or more jobs at once, and your setup time is split evenly across them. Part counting is also off during setup, so remember to enter your actual counts when you submit each setup labor ticket."
                )
              : t(
                  "Automatic program matching takes over from here. MachineMetrics watches the running program, then detects and switches jobs for you. You won't be able to add jobs by hand again until you go back to setup."
                )
          }
          cancelText={t('Cancel')}
          confirmText={t('Confirm')}
          onConfirm={handleConfirmTransition}
          onCancel={() => {
            return setPendingTransition(null);
          }}
        />
      )}

      {/* Opaque transition overlay — see `transitioning`. Reuses the sign-in
          flow's BlockingOverlay (pointer-events: all) so the setup⇄production
          switch blocks tap-through into stale UI beneath the just-dismissed
          confirm modal and shows the operator clear feedback. */}
      <BlockingOverlay
        isVisible={!!transitioning}
        title={
          transitioning === 'production'
            ? t('Entering production…')
            : t('Entering setup…')
        }
      />

      {/* Select Jobs drawer (multi-select) — apm-select-jobs MFE */}
      <MMDrawer
        isOpen={selectJobsOpen}
        onClose={closeSelectJobs}
        title="Select Jobs"
        hideHeader
      >
        <Box
          flex={1}
          minH={0}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          h="calc(100vh - 2rem)"
        >
          <MultiJobSelectFlow
            machineRef={machineRef}
            machineId={machine?.id}
            erpResources={erpResources}
            onCancel={closeSelectJobs}
            onComplete={closeSelectJobs}
          />
        </Box>
      </MMDrawer>

      {/* Add Job drawer — apm-select-jobs multi-select flow (same as Select Jobs) */}
      <MMDrawer
        isOpen={addJobOpen}
        onClose={closeAddJob}
        title="Add Job"
        hideHeader
      >
        <Box
          flex={1}
          minH={0}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          h="calc(100vh - 2rem)"
        >
          {/* Add Job uses the same multi-select "Select jobs to set up" flow
              as Select Jobs (demo parity) — the legacy single-select +
              per-job confirm flow is retired. */}
          <MultiJobSelectFlow
            machineRef={machineRef}
            machineId={machine?.id}
            erpResources={erpResources}
            onCancel={closeAddJob}
            onComplete={closeAddJob}
          />
        </Box>
      </MMDrawer>

      {/* Program event form drawer */}
      <MMDrawer
        isOpen={eventFormOpen}
        onClose={handleEventFormClose}
        title="Submit work"
      >
        {eventFormContext && (
          <MMEmbeddableZoneWithLoader
            id="labor-ticket-event-form"
            src={LABOR_TICKET_HOST}
            path={`/program-event/${eventFormContext.machineRef}/${eventFormContext.eventId}`}
            context={eventFormEmbedContext}
          />
        )}
      </MMDrawer>
    </>
  );
};

export { ERPMultiJobControlBar };
